/**
 * Finder host mode: the same platform bundle, served on the Finder host,
 * behaves as the directory (clean URLs, own brand bar, public pages).
 * Activated when the backend sets APP_SETTINGS.host_mode to "finder"
 * (the Finder extension's context processor does so on that host).
 */
const FINDER_PREFIX = "/finder";

// Top-level routes registered WITHOUT the /finder prefix (React auth pages,
// the public receipt verifier) must bypass prefixing or they render blank.
const isPassthrough = (p: string) => p.startsWith("/auth/") || p.startsWith("/verify-receipt");

const toBrowserPath = (p: string) => {
  if (!p) return "/";
  if (p === FINDER_PREFIX) return "/";
  if (p.startsWith(`${FINDER_PREFIX}/`)) return p.slice(FINDER_PREFIX.length);
  return p;
};

const toRouterPath = (p: string) => {
  if (!p || p === "/") return FINDER_PREFIX;
  if (isPassthrough(p)) return p;
  if (p === FINDER_PREFIX || p.startsWith(`${FINDER_PREFIX}/`)) return p;
  return FINDER_PREFIX + p;
};

const PAGE_TITLES: Array<[RegExp, string]> = [
  [/^\/finder\/me/, "My profile"],
  [/^\/finder\/settings/, "Settings"],
  [/^\/finder\/asks\/new/, "New ask"],
  [/^\/finder\/asks\/\d/, "Ask"],
  [/^\/finder\/asks/, "Asks"],
  [/^\/finder\/activity/, "Activity"],
  [/^\/finder\/docs/, "How it works"],
  [/^\/finder\/apply/, "Join"],
  [/^\/finder\/admin/, "Review"],
  [/^\/finder\/u\//, "Profile"],
  [/^\/finder\/messages/, "Messages"],
  [/^\/finder\/?$/, "People"],
];

export const finderHostMode = {
  key: "finder",
  isActive: (settings: { host_mode?: string }) => settings?.host_mode === "finder",
  rootRedirect: FINDER_PREFIX,
  baseTitle: "AfriFinder",
  pageTitle: (pathname: string) => {
    for (const [re, title] of PAGE_TITLES) if (re.test(pathname)) return title;
    return undefined;
  },
  chromelessPrefixes: [FINDER_PREFIX],
  // The directory ships its own footer and bottom navigation.
  appFooter: false,
  // Docs already sit in the left rail and the mobile tab bar, so the
  // floating link is a second route to the same page, hovering over
  // the content on every screen.
  docsFab: false,
  // Same for the platform's testing-programme Report button: the
  // directory has its own reporting, on the things there are to report
  // on, and a float from another product hovers over every page here.
  reportFab: false,
  allowAnonymous: true,
  // The directory runs without a service worker: the server answers
  // /sw.js on this host with a self-destructing kill-switch (see the
  // Finder middleware), so the platform must never register it on a
  // clean visit — doing so reloads the tab on every load.
  noServiceWorker: true,
  pathMapping: { toRouterPath, toBrowserPath },
  brand: {
    lockup: "/static/brand/afrifinder/lockup.png?v=18",
    lockupDark: "/static/brand/afrifinder/lockup-dark.png?v=18",
    alt: "AfriFinder",
    homeHref: "/",
  },
  // Auth pages (sign in, sign up, reset) wear the Finder brand here. The
  // payout-receipt check belongs to the annotation platform, not the directory.
  authBrand: {
    name: "AfriFinder",
    tagline: "A verified directory of students, practitioners, and experts in African AI and NLP.",
    markUrl: "/static/brand/afrifinder/mark.png",
    markUrlDark: "/static/brand/afrifinder/mark-dark.png",
    receiptLink: false,
  },
  // Browser paths, not router paths. The platform renders both as plain
  // <a href> (the handbook float and the profile item in the user menu),
  // so whatever is written here is what a person sees on hover and gets
  // from "copy link". The prefix is an internal detail of the router on
  // this host -- ``toRouterPath`` puts it back -- and must not appear in
  // a URL. ``/docs`` reaches the handbook rather than the platform's
  // docs-site proxy because the middleware rewrites it to ``/finder/docs``
  // before the URL conf is consulted: ``/docs`` is not in PASSTHROUGH.
  docsHref: "/docs",
  docsLabel: "AfriFinder handbook",
  notificationsScope: "finder",
  profileHref: "/me",
  // The directory is public: sign in through the React page and land back
  // on the public home after signing out.
  signInHref: "/auth/login",
  signedOutHref: "/",
};
